import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { BookSearchResult } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { BookSearch } from "./BookSearch";
import { Cover, authorsOf } from "./BookBits";
import { CloseIcon } from "./Icons";
import { useScrollLock, useSheetDrag } from "./ui";

/*
 * "Pick the first book" and "Add books you've read before": search the
 * catalog, confirm, and the book goes straight onto the shelf, as the
 * current book or onto Finished (with dates, if known). Admins only; one
 * call (POST books/place), so it's never a suggestion first.
 */
export function PickBook({ clubId, mode, first, onClose, onDone }:
  { clubId: string; mode: "current" | "finished"; first?: boolean; onClose: () => void; onDone?: () => void }) {
  useScrollLock();
  const qc = useQueryClient();
  const sheet = useRef<HTMLDivElement>(null);
  useSheetDrag(sheet, onClose);
  const [book, setBook] = useState<BookSearchResult | null>(null);
  const [startedAt, setStartedAt] = useState("");
  const [finishedAt, setFinishedAt] = useState("");
  const place = useMutation({
    mutationFn: () => api.placeBook(clubId, { hardcoverId: book!.hardcoverId, status: mode, ...(mode === "finished" ? { startedAt: startedAt || null, finishedAt: finishedAt || undefined } : {}) }),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: ["books", clubId] }), qc.invalidateQueries({ queryKey: ["club-stats", clubId] }), qc.invalidateQueries({ queryKey: ["club-activity", clubId] })]);
      if (mode === "finished") { setBook(null); setStartedAt(""); setFinishedAt(""); } // add another, or close
      else onClose();
      onDone?.();
    },
  });
  const title = mode === "current" ? (first ? "Pick the first book" : "Pick the next book") : "Add a book you've read before";
  return createPortal(
    <>
      <div className="modal-dim" onClick={onClose} />
      <div className="modal pick-sheet" role="dialog" aria-label={title} ref={sheet}>
        <div className="grab" />
        <div className="head"><b>{title}</b><button type="button" aria-label="Close" onClick={onClose}><CloseIcon /></button></div>
        <div className="pick-body">
          {!book ? (
            <>
              <p className="muted small" style={{ margin: 0 }}>{mode === "current" ? "It becomes the club's book straight away, and everyone's progress starts here." : "It goes straight onto the Finished shelf. Add as many as you like."}</p>
              <BookSearch onPick={setBook} />
              {mode === "finished" && place.isSuccess && <p className="ok small" style={{ margin: 0 }}>Added. Search for another, or close.</p>}
            </>
          ) : (
            <form className="stack" onSubmit={(e) => { e.preventDefault(); place.mutate(); }}>
              <div className="pick-chosen">
                <Cover book={book} size="sm" />
                <div style={{ minWidth: 0, flex: 1 }}><div className="t">{book.title}</div><div className="muted small">{authorsOf(book)}{book.releaseYear ? ` · ${book.releaseYear}` : ""}</div></div>
                <button type="button" className="btn ghost sm" onClick={() => setBook(null)}>Change</button>
              </div>
              {mode === "current" ? (
                <p style={{ margin: 0 }}>Start <i>{book.title}</i> for the club?</p>
              ) : (
                <div className="pick-dates">
                  <div className="field"><label htmlFor="pk-start">Started (optional)</label><input id="pk-start" className="input" type="date" value={startedAt} onChange={(e) => setStartedAt(e.target.value)} /></div>
                  <div className="field"><label htmlFor="pk-end">Finished (optional)</label><input id="pk-end" className="input" type="date" value={finishedAt} onChange={(e) => setFinishedAt(e.target.value)} /></div>
                </div>
              )}
              {place.isError && <p className="error small">{describeError(place.error)}</p>}
              <div className="row" style={{ gap: 8 }}>
                <button type="submit" className="btn primary" disabled={place.isPending}>{place.isPending ? "Working…" : mode === "current" ? "Start it" : "Add to Finished"}</button>
                <button type="button" className="btn ghost" onClick={() => setBook(null)}>Back</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
